import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

export interface LoginPayload {
  document?: string; // CPF/CNPJ (preferido)
  email?: string;    // compatibilidade
  password: string;
}

export interface LoginResponse {
  token: string;
  token_type?: string;
  expires_in?: number;
  user?: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private storageKey = 'ld_token';
  private userKey = 'ld_user';
  private _user: any | null = null;

  constructor(private http: HttpClient) {
    // Hydrate user from storage on startup
    const stored = this.getFromStorage(this.userKey);
    if (stored) {
      try { this._user = JSON.parse(stored); } catch { this._user = null; }
    } else {
      // Try to infer minimal user info from token (roles), if any
      const t = this.getToken();
      if (t) {
        const payload = this.decodeJwt(t);
        this._user = this.userFromPayload(payload);
      }
    }
  }

  // Switch active establishment for EMPLOYEE and persist refreshed token
  switchActiveEstablishment(establishmentId: number): Observable<{ token: string; token_type?: string; expires_in?: number; active_establishment_id?: number; }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<{ token: string; token_type?: string; expires_in?: number; active_establishment_id?: number; }>(
      '/api/v1/employees/me/switch-establishment',
      { establishment_id: establishmentId },
      { headers }
    ).pipe(
      tap(res => {
        if (res?.token) {
          this.setInStorage(this.storageKey, res.token);
          // keep existing user, but we may want to reflect active establishment locally
          if (this._user) {
            (this._user as any).active_establishment_id = res.active_establishment_id ?? establishmentId;
            this.setInStorage(this.userKey, JSON.stringify(this._user));
          }
        }
      })
    );
  }

  login(payload: LoginPayload): Observable<LoginResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<LoginResponse>('/api/v1/auth/login', payload, { headers }).pipe(
      tap((res) => {
        if (res && res.token) {
          // prefer localStorage; fallback to sessionStorage
          this.setInStorage(this.storageKey, res.token);
        }
        // Persist user info if provided; otherwise derive from token claims
        const u = res?.user ?? this.userFromPayload(this.decodeJwt(res?.token || ''));
        if (u) {
          this._user = u;
          this.setInStorage(this.userKey, JSON.stringify(u));
        }
      })
    );
  }

  logout(): void {
    this.removeFromStorage(this.storageKey);
    this.removeFromStorage(this.userKey);
    this._user = null;
  }

  // Compatibility: property accessor used by guards/interceptors
  get token(): string | null {
    return this.getToken();
  }

  // Current user info used by templates (e.g., app shell)
  get user(): any | null {
    return this._user;
  }

  hasRole(role: string): boolean {
    if (!role) return false;
    const wanted = role.toUpperCase();
    const token = this.getToken();
    const roles = new Set<string>();

    // Prefer roles from user object
    const u = this._user;
    const push = (r: any) => { if (typeof r === 'string' && r) roles.add(r.toUpperCase()); };
    if (u) {
      if (Array.isArray(u.roles)) u.roles.forEach(push);
      if (u.role) push(u.role);
      if (u.type) push(u.type);
      if (u.profile) push(u.profile);
    }

    // Fallback: decode JWT claims for roles
    if (token && roles.size === 0) {
      const payload = this.decodeJwt(token);
      const pr = payload?.roles ?? payload?.role ?? payload?.typ ?? payload?.type ?? payload?.profile;
      if (Array.isArray(pr)) pr.forEach(push);
      else push(pr);
    }

    return roles.has(wanted);
  }

  getToken(): string | null {
    const ls = this.getFromStorage(this.storageKey);
    const ss = this.getFromStorage(this.storageKey, true);
    return ls || ss;
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  // Password recovery
  requestPasswordReset(email: string) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<{ message: string }>('/api/v1/auth/forgot-password', { email }, { headers });
  }

  resetPassword(token: string, password: string) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<{ message: string }>('/api/v1/auth/reset-password', { token, new_password: password }, { headers });
  }

  resendVerification(email: string, actor: 'customer' | 'establishment' = 'customer') {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<{ status: string; verify_email: string }>('/api/v1/auth/resend-verification', { email, actor }, { headers });
  }

  // Helpers for safe storage access
  private getFromStorage(key: string, session = false): string | null {
    try {
      return session ? sessionStorage.getItem(key) : localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setInStorage(key: string, value: string): void {
    try { localStorage.setItem(key, value); }
    catch { try { sessionStorage.setItem(key, value); } catch {} }
  }

  private removeFromStorage(key: string): void {
    try { localStorage.removeItem(key); } catch {}
    try { sessionStorage.removeItem(key); } catch {}
  }

  private decodeJwt(token: string): any | null {
    if (!token || token.split('.').length < 2) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    try {
      const json = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      return JSON.parse(json);
    } catch {
      try {
        // Fallback simple atob/JSON.parse
        return JSON.parse(atob(base64));
      } catch {
        return null;
      }
    }
  }

  private userFromPayload(payload: any): any | null {
    if (!payload || typeof payload !== 'object') return null;
    const roles = Array.isArray(payload.roles) ? payload.roles : (payload.role ? [payload.role] : (payload.type ? [payload.type] : []));
    return {
      id: payload.sub || payload.id || null,
      email: payload.email || null,
      name: payload.name || payload.given_name || null,
      roles
    };
  }
}
